// The printed imaging report (docs/plans/phase2-radiology.md §1.1 item 7): A4, black on
// white, identifiers in the mono face. An unverified report says so in its title and
// footer (plan R2); an amended one names its version and the reason.
import { useMutation } from '@tanstack/react-query';
import type { ImagingReportView } from '@wodi/shared';
import {
  Button,
  Dialog,
  DialogActions,
  formatShortAge,
  Notice,
  PrintFooter,
  PrintHeader,
  PrintLines,
  PrintSheet,
  printSheet,
} from '@wodi/ui';
import { useRef, useState, type Ref } from 'react';

import { recordImagingReportPrinted } from '../api/imaging.ts';
import { formatInstant } from '../patients/format.ts';
import { errorText } from '../stock/common.tsx';
import { modalityWords, PRELIMINARY } from './format.ts';
import styles from './imaging.module.css';

export interface ImagingPrintData {
  facilityName: string;
  patient: {
    displayName: string;
    patientNumber: string;
    sex: string;
    age: { unit: 'days' | 'months' | 'years'; value: number } | null;
  };
  report: ImagingReportView;
  /** Which copy this is, once printed before. */
  copy: number;
}

export function ImagingReportSheet({
  data,
  sheetRef,
}: {
  data: ImagingPrintData;
  sheetRef?: Ref<HTMLElement>;
}) {
  const { report } = data;
  const version = report.current;
  const verified = report.status === 'verified';
  const title = `${verified ? 'Imaging report' : 'Preliminary imaging report'}${
    report.currentVersion > 1 ? `, version ${String(report.currentVersion)}` : ''
  }${data.copy > 1 ? `, copy ${String(data.copy)}` : ''}`;
  return (
    <PrintSheet paper="a4" label={title} {...(sheetRef ? { ref: sheetRef } : {})}>
      <PrintHeader facility={data.facilityName} title={title} />
      <PrintLines
        items={[
          { label: 'Patient', value: data.patient.displayName },
          { label: 'Patient no.', value: data.patient.patientNumber, identifier: true },
          {
            label: 'Sex and age',
            value: `${data.patient.sex}, ${formatShortAge(data.patient.age, false)}`,
          },
          { label: 'Examination', value: `${report.itemName} (${modalityWords(report.modality)})` },
          { label: 'Requested by', value: report.orderingUser.displayName },
          ...(report.performedAt === null
            ? []
            : [{ label: 'Performed', value: formatInstant(report.performedAt) }]),
        ]}
      />
      <div className={styles.sheetBody}>
        {version.findings === null ? null : (
          <>
            <p className={styles.sheetHeading}>Findings</p>
            <p className={styles.sheetText}>{version.findings}</p>
          </>
        )}
        <p className={styles.sheetHeading}>Impression</p>
        <p className={styles.sheetText}>{version.impression}</p>
        {version.amendmentReason === null ? null : (
          <p className={styles.sheetText}>
            <strong>Amended report.</strong> {version.amendmentReason}
          </p>
        )}
      </div>
      <PrintLines
        items={[
          {
            label: 'Reported by',
            value: `${version.signedBy.displayName}, ${formatInstant(version.signedAt)}`,
          },
          ...(verified && report.verifiedBy !== null && report.verifiedAt !== null
            ? [
                {
                  label: 'Verified by',
                  value: `${report.verifiedBy.displayName}, ${formatInstant(report.verifiedAt)}`,
                },
              ]
            : []),
        ]}
      />
      <PrintFooter>{verified ? 'Verified report.' : `${PRELIMINARY}.`}</PrintFooter>
    </PrintSheet>
  );
}

/**
 * A preview and "Print". Each print is recorded, with its copy number and version, before
 * it is sent to the printer (the print of a clinical document; CLAUDE.md, audit). The copy
 * number counts within this preview (plan R15).
 */
export function PrintImagingReportDialog({
  data,
  onClose,
}: {
  data: Omit<ImagingPrintData, 'copy'>;
  onClose: () => void;
}) {
  const sheet = useRef<HTMLElement>(null);
  const [copy, setCopy] = useState(1);
  const record = useMutation({
    mutationFn: (n: number) =>
      recordImagingReportPrinted(data.report.id, n, data.report.currentVersion),
    // Recorded first, then printed: no copy leaves the printer unaudited.
    onSuccess: (_d, n) => {
      if (sheet.current !== null) printSheet(sheet.current);
      setCopy(n + 1);
    },
  });
  return (
    <Dialog
      title="Print report"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      {/* Scrolls on small screens, so it takes focus for keyboard users (axe). */}
      <div className={styles.preview} tabIndex={0} role="region" aria-label="Report preview">
        <ImagingReportSheet sheetRef={sheet} data={{ ...data, copy }} />
      </div>
      {record.isError ? (
        <Notice tone="error">
          {errorText(record.error, "The print wasn't recorded. Print again to record it.")}
        </Notice>
      ) : null}
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Close
        </Button>
        <Button
          variant="primary"
          isPending={record.isPending}
          pendingLabel="Recording…"
          onPress={() => {
            record.mutate(copy);
          }}
        >
          Print
        </Button>
      </DialogActions>
    </Dialog>
  );
}
